// P6 · Cierre y rendición (documento 07, documento 06 F8). Lo que se rinde se calcula con la misma función que la API.
import { Boton, Chip, cx, Icono, Tarjeta, useToast } from '@reparto/ui';
import { formatoGs, formatoHora, MEDIO_COBRO_ETIQUETA, MOTIVO_NO_ENTREGA_ETIQUETA } from '@reparto/shared/nucleo';
import { type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router';
import { cerrar } from '../datos/acciones';
import { useEstado } from '../datos/ganchos';
import { soltarMovil } from '../datos/paquete';
import { rendicionPrevia } from '../datos/vista';
import { EsqueletoCierre } from '../componentes/Esqueletos';
import { Marco } from '../componentes/Marco';

function Fila({
  etiqueta,
  valor,
  fuerte,
  tono,
}: {
  etiqueta: ReactNode;
  valor: ReactNode;
  fuerte?: boolean;
  tono?: 'bien' | 'mal';
}) {
  return (
    <div className={cx('flex items-baseline justify-between gap-3 py-1.5', fuerte && 'text-base font-semibold')}>
      <span className={cx(!fuerte && 'text-stone-600 dark:text-stone-300')}>{etiqueta}</span>
      <span
        className={cx(
          'tabular-nums',
          tono === 'mal' && 'text-rose-700 dark:text-rose-300',
          tono === 'bien' && 'text-primary-700 dark:text-primary-300',
        )}
      >
        {valor}
      </span>
    </div>
  );
}

export function Cierre() {
  const navegar = useNavigate();
  const { mostrar } = useToast();
  const { vista, cola } = useEstado();
  const [observacion, setObservacion] = useState('');
  const [cerrando, setCerrando] = useState(false);
  const ruta = vista?.ruta;
  if (!vista || !ruta) return <EsqueletoCierre />;
  const pendientes = cola.filter((o) => o.estado === 'pendiente' || o.estado === 'enviando').length;
  const cerrada = ruta.estado === 'cerrada';
  const r = cerrada && ruta.rendicion ? ruta.rendicion : rendicionPrevia(ruta);
  const sinVisitar = ruta.paradas.filter((p) => p.resultado === 'pendiente').length;
  const tolerancia = vista.configuracion.rendicion.tolerancia_diferencia;
  const diferenciaMala = Math.abs(r.diferencia) > tolerancia;

  async function confirmar() {
    setCerrando(true);
    try {
      await cerrar(ruta!.id, observacion.trim() || undefined);
      mostrar('Rendición guardada', { tipo: 'ok' });
    } finally {
      setCerrando(false);
    }
  }

  // En pantallas anchas los botones van en una fila a la derecha, debajo del detalle y la observación.
  const pie = cerrada ? (
    pendientes === 0 ? (
      <div className="lg:flex lg:justify-end">
        <Boton
          bloque
          variante="secundario"
          icono="swap_horiz"
          onClick={() => void soltarMovil().then(() => navegar('/movil', { replace: true }))}
          className="lg:w-auto lg:min-w-72"
        >
          Terminar y elegir otro móvil
        </Boton>
      </div>
    ) : null
  ) : (
    <div className="space-y-2 pb-1 lg:flex lg:items-center lg:justify-end lg:gap-3 lg:space-y-0">
      {sinVisitar ? (
        <Boton bloque variante="secundario" icono="arrow_back" onClick={() => navegar('/ruta')} className="lg:w-auto">
          Volver a la ruta
        </Boton>
      ) : null}
      <Boton
        bloque
        tamano="lg"
        variante="acento"
        icono="receipt_long"
        cargando={cerrando}
        onClick={() => void confirmar()}
        className="lg:w-auto lg:min-w-80"
      >
        Confirmar rendición
      </Boton>
    </div>
  );

  return (
    <Marco
      titulo={cerrada ? 'Rendición' : 'Cerrar ruta y rendir'}
      subtitulo={`${vista.movil.nombre}${ruta.cierre_en ? ` · cerrada ${formatoHora(ruta.cierre_en)}` : ''}`}
      paso="cierre"
      pie={pie}
      ancho="amplio"
    >
      {/* Celular y tablet vertical: una columna. Pantallas anchas: resumen | detalle por parada y observación. */}
      <div className="space-y-3 p-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-4 lg:space-y-0">
        <div className="space-y-3">
          {cerrada ? (
            r.estado === 'recibida' ? (
              <div className="flex gap-3 rounded-2xl bg-primary-700 p-4 text-white">
                <Icono nombre="verified" tamano={32} relleno />
                <div>
                  <div className="font-medium">Rendición recibida</div>
                  <div className="text-sm text-primary-100">
                    {r.recibida_por_nombre ? `Por ${r.recibida_por_nombre}` : 'Por el depósito'}
                    {r.recibida_en ? ` a las ${formatoHora(r.recibida_en)}` : ''}. ¡Buen trabajo!
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex gap-3 rounded-2xl bg-stone-900 p-4 text-white dark:bg-stone-100 dark:text-stone-900">
                <Icono nombre={pendientes ? 'cloud_upload' : 'outgoing_mail'} tamano={32} />
                <div>
                  <div className="font-medium">
                    {pendientes ? 'Rendición guardada en el celular' : 'Rendición enviada'}
                  </div>
                  <div className="text-sm opacity-80">
                    {pendientes
                      ? 'Se envía sola cuando haya señal. No cierres sesión.'
                      : 'Entregá el efectivo en el depósito. Te avisamos cuando la reciban.'}
                  </div>
                </div>
              </div>
            )
          ) : sinVisitar ? (
            <p className="flex gap-2 rounded-2xl bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
              <Icono nombre="warning" className="shrink-0" />
              Quedan {sinVisitar} paradas sin resolver. Si cerrás ahora, se registran como “sin visitar” y sus bultos
              vuelven al depósito.
            </p>
          ) : null}

          <Tarjeta className="bg-amber-50 ring-1 ring-amber-200 dark:bg-amber-900/20 dark:ring-amber-800">
            <div className="text-xs tracking-wide text-amber-800 uppercase dark:text-amber-200">Efectivo a rendir</div>
            <div className="text-3xl font-bold tabular-nums">{formatoGs(r.efectivo_cobrado)}</div>
            {r.efectivo_esperado !== r.efectivo_cobrado ? (
              <div className="text-sm text-stone-600 dark:text-stone-300">
                Esperado en efectivo {formatoGs(r.efectivo_esperado)}
              </div>
            ) : null}
          </Tarjeta>

          <Tarjeta>
            <h2 className="mb-1 text-sm font-medium">Cobros</h2>
            <div className="divide-y divide-stone-100 text-sm dark:divide-stone-800">
              <Fila etiqueta="Efectivo" valor={formatoGs(r.efectivo_cobrado)} />
              <Fila etiqueta="QR" valor={formatoGs(r.qr_cobrado)} />
              <Fila etiqueta="Transferencia" valor={formatoGs(r.transferencia_cobrada)} />
              <Fila etiqueta="Cuenta corriente" valor={formatoGs(r.cuenta_corriente)} />
              <Fila etiqueta="Esperado (sin cuenta corriente)" valor={formatoGs(r.cobrable_esperado)} />
              <Fila
                etiqueta="Diferencia"
                valor={`${r.diferencia > 0 ? '+' : r.diferencia < 0 ? '−' : ''}${formatoGs(Math.abs(r.diferencia))}`}
                fuerte
                tono={diferenciaMala ? 'mal' : 'bien'}
              />
            </div>
          </Tarjeta>

          <div className="grid grid-cols-2 gap-3">
            <Tarjeta>
              <h2 className="mb-1 text-sm font-medium">Bultos</h2>
              <div className="text-sm">
                <Fila etiqueta="Cargados" valor={r.bultos_cargados} />
                <Fila etiqueta="Entregados" valor={r.bultos_entregados} />
                <Fila etiqueta="Vuelven" valor={r.bultos_devueltos} tono={r.bultos_devueltos ? 'mal' : undefined} />
              </div>
            </Tarjeta>
            <Tarjeta>
              <h2 className="mb-1 text-sm font-medium">Paradas</h2>
              <div className="text-sm">
                <Fila etiqueta="Completas" valor={r.paradas_entregadas} />
                <Fila etiqueta="Parciales" valor={r.paradas_parciales} />
                <Fila
                  etiqueta="No entregadas"
                  valor={r.paradas_fallidas}
                  tono={r.paradas_fallidas ? 'mal' : undefined}
                />
              </div>
            </Tarjeta>
          </div>
        </div>

        <div className="space-y-3">
          <Tarjeta relleno="none">
            <h2 className="px-4 pt-3 pb-1 text-sm font-medium">Detalle por parada</h2>
            <ul className="divide-y divide-stone-100 dark:divide-stone-800">
              {ruta.paradas.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => navegar(`/parada/${p.id}`)}
                    className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm"
                  >
                    <span className="w-5 text-stone-500 tabular-nums">{p.orden}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{p.cliente.nombre}</span>
                      <span className="block text-xs text-stone-500">
                        {p.resultado === 'pendiente'
                          ? 'Sin resolver'
                          : p.resultado === 'no_entregada'
                            ? `No entregada${p.motivo ? ` · ${MOTIVO_NO_ENTREGA_ETIQUETA[p.motivo]}` : ''}`
                            : `${p.resultado === 'parcial' ? 'Parcial' : 'Entregada'}${p.cobro ? ` · ${MEDIO_COBRO_ETIQUETA[p.cobro.medio]}` : ''}`}
                      </span>
                    </span>
                    <span className="tabular-nums">
                      {p.cobro && p.cobro.medio !== 'cuenta_corriente' ? formatoGs(p.cobro.monto_cobrado) : '—'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Tarjeta>

          {cerrada ? (
            r.observacion_chofer ? (
              <p className="text-sm text-stone-600 dark:text-stone-300">Tu observación: {r.observacion_chofer}</p>
            ) : null
          ) : (
            <textarea
              className="min-h-20 w-full rounded-2xl border border-stone-300 bg-white p-3 text-[15px] dark:border-stone-600 dark:bg-stone-900"
              placeholder="Observación para el encargado (opcional)"
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              maxLength={500}
            />
          )}
          {cerrada && r.estado !== 'recibida' ? (
            <div className="flex justify-center">
              <Chip tono="aviso" icono="hourglass_top">
                Esperando que el depósito la reciba
              </Chip>
            </div>
          ) : null}
        </div>
      </div>
    </Marco>
  );
}
